import { Component, ErrorInfo, ReactNode } from "react"
import LogRocket from "logrocket"

type ErrorBoundaryProps = {
  children: ReactNode
  // Optional custom fallback. Receives a reset callback that clears the error
  // state and re-attempts a render of the children.
  fallback?: (reset: () => void) => ReactNode
  // Label used to identify this boundary in LogRocket/console reports.
  name?: string
}

type ErrorBoundaryState = {
  hasError: boolean
}

// Generic error boundary. Wrap around any subtree that shouldn't be able to
// take down the whole app when it throws during render. Reports the error to
// LogRocket so we retain the crash context that a torn-down React tree would
// otherwise lose.
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const label = this.props.name ?? "ErrorBoundary"
    console.error(`[${label}] caught render error`, error, info.componentStack)
    LogRocket.captureException(error, {
      tags: { boundary: label },
      extra: { componentStack: info.componentStack ?? "" },
    })
  }

  reset = () => {
    this.setState({ hasError: false })
  }

  render() {
    if (!this.state.hasError) return this.props.children

    if (this.props.fallback) return this.props.fallback(this.reset)

    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
        <h2 className="text-lg font-semibold text-gray-900">
          Something went wrong
        </h2>
        <p className="max-w-md text-sm text-gray-600">
          This part of the simulation ran into an unexpected error. Your
          progress has been saved — try again, or reload the page if the problem
          persists.
        </p>
        <button
          className="bg-brand-blue hover:bg-brand-blue-hover rounded-lg px-6 py-2 font-semibold text-white transition-colors"
          onClick={this.reset}>
          Try again
        </button>
      </div>
    )
  }
}

export default ErrorBoundary
